<div class="podcast-player" (contextmenu)="$event.preventDefault()">

  <!-- Hidden audio element -->
  <audio
    #audioElement
    [src]="audioUrl"
    (loadedmetadata)="onLoadedMetadata()"
    (timeupdate)="onTimeUpdate()"
    (ended)="onAudioEnded()"
    (pause)="onAudioPaused()"
    preload="metadata"
  ></audio>

  <!-- ═══════════════════════════════════════════════════════════════
       DESKTOP  ≥ 1024 px
  ════════════════════════════════════════════════════════════════ -->
  <div *ngIf="!showTranscript" class="hidden lg:flex flex-col gap-4 py-4 px-5">

    <!-- Header row -->
    <div class="flex items-center gap-3">

      <!-- Icon badge -->
      <div class="icon-badge">
        <ion-icon name="headset"></ion-icon>
      </div>

      <!-- Metadata -->
      <div class="flex flex-col leading-none">
        <div class="flex items-center gap-[6px]">
          <span class="overline-label">{{ moduleLabel }}</span>
          <span class="counter-label">{{ getModuleCounter() }}</span>
        </div>
        <span class="title-label text-lg">{{ audioTitle }}</span>
      </div>

    </div>

    <!-- Waveform -->
    <div class="waveform-row" [class.playing]="isPlaying">
      <div
        *ngFor="let bar of waveformBars; let i = index"
        class="wf-bar"
        [class.played]="isBarPlayed(i)"
        [style.height.px]="(bar / 100) * 54"
      ></div>
    </div>

    <!-- Progress bar + timestamps -->
    <div class="flex flex-col gap-1">
      <div class="progress-wrap">
        <div class="progress-track">
          <div class="progress-filled" [style.width.%]="progress"></div>
          <div class="progress-thumb" [style.left.%]="progress"></div>
        </div>
        <input
          type="range"
          class="progress-range-overlay"
          [value]="currentTime"
          [min]="0"
          [max]="duration || 1"
          step="0.1"
          (input)="onSeek($event)"
          aria-label="Progreso de audio"
        />
      </div>
      <div class="flex justify-between items-center">
        <span class="timestamp-label">{{ formatTime(currentTime) }}</span>
        <span class="remaining-label">{{ getTimeRemaining() }} restantes</span>
        <span class="timestamp-label">{{ formatTime(duration) }}</span>
      </div>
    </div>

    <!-- Transport controls -->
    <div class="grid items-center grid-cols-[auto_1fr_auto] gap-3">

      <!-- Volume -->
      <div class="volume-control">
        <button
          class="ctrl-btn ctrl-sm"
          (click)="toggleMute()"
          aria-label="Bajar volumen"
        >
          <ion-icon [name]="volumeIcon"></ion-icon>
        </button>
        <div class="volume-wrap">
          <div class="volume-track">
            <div class="volume-filled" [style.width.%]="isMuted ? 0 : volume * 100"></div>
            <div class="volume-thumb" [style.left.%]="isMuted ? 0 : volume * 100"></div>
          </div>
          <input
            type="range"
            class="volume-range-overlay"
            [min]="MIN_VOLUME"
            max="1"
            step="0.02"
            [value]="volume"
            (input)="onVolumeChange($event)"
            aria-label="Volumen"
          />
        </div>
      </div>

      <div class="flex items-center justify-center gap-3" aria-label="Controles de reproducción">
        <!-- Skip back 15s -->
        <button
          class="ctrl-btn ctrl-md"
          (click)="skipBack()"
          aria-label="Retroceder 15 segundos"
        >
          <ion-icon name="play-skip-back"></ion-icon>
        </button>

        <!-- Play / Pause (primary) -->
        <button
          class="ctrl-btn ctrl-lg ctrl-primary"
          (click)="togglePlayPause()"
          [attr.aria-label]="isPlaying ? 'Pausar' : 'Reproducir'"
        >
          <ion-icon
            [name]="isPlaying ? 'pause' : 'play'"
            [style.marginLeft.px]="isPlaying ? 0 : 2"
          ></ion-icon>
        </button>

        <!-- Skip forward 15s -->
        <button
          class="ctrl-btn ctrl-md"
          (click)="skipForward()"
          aria-label="Adelantar 15 segundos"
        >
          <ion-icon name="play-skip-forward"></ion-icon>
        </button>
      </div>

      <div class="flex items-center justify-end gap-3">
        <!-- Speed — desktop only -->
        <div class="speed-control-wrap ">
          <button
            class="ctrl-btn ctrl-sm ctrl-purple"
            (click)="toggleSpeedMenu()"
            aria-label="Cambiar velocidad de reproducción"
          >
            <mat-icon class="mdi mdi-speedometer speed-icon"></mat-icon>
          </button>
          <div *ngIf="showSpeedMenu" class="speed-menu" (mouseleave)="showSpeedMenu = false">
            <button
              *ngFor="let speed of speedOptions"
              class="speed-option-btn"
              (click)="setPlaybackSpeed(speed)"
            >
              <span class="speed-option-item" [class.active]="speed === playbackSpeed">
                {{ speed === 1 ? 'Normal' : speed + 'x' }}
              </span>
            </button>
          </div>
        </div>

        <!-- Transcript -->
        <button
          *ngIf="hasTranscript"
          class="ctrl-btn ctrl-sm ctrl-purple"
          (click)="toggleTranscript()"
          aria-label="Ver transcripción"
        >
          <ion-icon name="document-text"></ion-icon>
        </button>

      </div>



    </div>

  </div><!-- /desktop -->


  <!-- ═══════════════════════════════════════════════════════════════
       MOBILE  < 1024 px
  ════════════════════════════════════════════════════════════════ -->
  <div *ngIf="!showTranscript" class="flex flex-col lg:hidden gap-4 p-5">

    <!-- Header row (speed button at top-right) -->
    <div class="flex items-center gap-3">

      <!-- Icon badge -->
      <div class="icon-badge">
        <ion-icon name="headset"></ion-icon>
      </div>

      <!-- Metadata -->
      <div class="flex flex-col leading-none flex-1 min-w-0">
        <div class="flex items-center gap-[6px]">
          <span class="overline-label">{{ moduleLabel }}</span>
          <span class="counter-label">{{ getModuleCounter() }}</span>
        </div>
        <span class="title-label text-left text-lg">{{ audioTitle }}</span>
      </div>

      <!-- Speed — mobile header -->
      <div class="speed-control-wrap flex-shrink-0">
        <button
          class="ctrl-btn ctrl-sm ctrl-purple"
          (click)="toggleSpeedMenu()"
          aria-label="Cambiar velocidad de reproducción"
        >
          <mat-icon class="mdi mdi-speedometer speed-icon"></mat-icon>
        </button>
        <div *ngIf="showSpeedMenu" class="speed-menu speed-menu-down" (mouseleave)="showSpeedMenu = false">
          <button
            *ngFor="let speed of speedOptions"
            class="speed-option-btn"
            (click)="setPlaybackSpeed(speed)"
          >
            <span class="speed-option-item" [class.active]="speed === playbackSpeed">
              {{ speed === 1 ? 'Normal' : speed + 'x' }}
            </span>
          </button>
        </div>
      </div>

    </div>

    <!-- Waveform -->
    <div class="waveform-row wf-mobile" [class.playing]="isPlaying">
      <div
        *ngFor="let bar of waveformBars; let i = index"
        class="wf-bar"
        [class.played]="isBarPlayed(i)"
        [style.height.px]="(bar / 100) * 72"
      ></div>
    </div>

    <!-- Action controls (single row: volume · transport · transcript) -->
    <div class="mobile-controls-row">

      <!-- Volume (icon only) -->
      <button
        class="ctrl-btn ctrl-sm"
        (click)="toggleMute()"
        aria-label="Bajar volumen"
      >
        <ion-icon [name]="volumeIcon"></ion-icon>
      </button>

      <!-- Transport controls (no speed button) -->
      <div class="flex items-center justify-center gap-3">

        <!-- Skip back 15s -->
        <button
          class="ctrl-btn ctrl-md"
          (click)="skipBack()"
          aria-label="Retroceder 15 segundos"
        >
          <ion-icon name="play-skip-back"></ion-icon>
        </button>

        <!-- Play / Pause (primary) -->
        <button
          class="ctrl-btn ctrl-lg ctrl-primary"
          (click)="togglePlayPause()"
          [attr.aria-label]="isPlaying ? 'Pausar' : 'Reproducir'"
        >
          <ion-icon
            [name]="isPlaying ? 'pause' : 'play'"
            [style.marginLeft.px]="isPlaying ? 0 : 2"
          ></ion-icon>
        </button>

        <!-- Skip forward 15s -->
        <button
          class="ctrl-btn ctrl-md"
          (click)="skipForward()"
          aria-label="Adelantar 15 segundos"
        >
          <ion-icon name="play-skip-forward"></ion-icon>
        </button>

      </div>

      <!-- Transcript -->
      <button
        *ngIf="hasTranscript"
        class="ctrl-btn ctrl-sm ctrl-purple"
        (click)="toggleTranscript()"
        aria-label="Ver transcripción"
      >
        <ion-icon name="document-text"></ion-icon>
      </button>

    </div>

    <!-- Progress bar at bottom -->
    <div class="flex flex-col gap-1">
      <div class="progress-wrap">
        <div class="progress-track">
          <div class="progress-filled" [style.width.%]="progress"></div>
          <div class="progress-thumb" [style.left.%]="progress"></div>
        </div>
        <input
          type="range"
          class="progress-range-overlay"
          [value]="currentTime"
          [min]="0"
          [max]="duration || 1"
          step="0.1"
          (input)="onSeek($event)"
          aria-label="Progreso de audio"
        />
      </div>
      <div class="flex justify-between">
        <span class="timestamp-label">{{ formatTime(currentTime) }}</span>
        <span class="timestamp-label">{{ formatTime(duration) }}</span>
      </div>
    </div>

  </div><!-- /mobile -->


  <!-- ═══════════════════════════════════════════════════════════════
       TRANSCRIPT MODE  (desktop + mobile, responsive)
  ════════════════════════════════════════════════════════════════ -->
  <div *ngIf="showTranscript" class="transcript-view">

    <!-- Header -->
    <div class="transcript-header flex items-center gap-3">

      <!-- Icon badge -->
      <div class="icon-badge transcript-icon">
        <ion-icon name="headset"></ion-icon>
      </div>

      <!-- Metadata -->
      <div class="flex flex-col leading-none flex-1 min-w-0">
        <div class="flex items-center gap-[6px]">
          <span class="overline-label">{{ moduleLabel }}</span>
          <span class="counter-label">{{ getModuleCounter() }}</span>
        </div>
        <span class="title-label text-left text-base opacity-80">{{ audioTitle }}</span>
      </div>

      <!-- Speed — mobile lives in the header (desktop keeps it in the control bar) -->
      <div *ngIf="!showTranscript" class="speed-control-wrap flex-shrink-0 lg:hidden">
        <button
          class="ctrl-btn ctrl-sm ctrl-purple"
          (click)="toggleSpeedMenu()"
          aria-label="Cambiar velocidad de reproducción"
        >
          <mat-icon class="mdi mdi-speedometer speed-icon"></mat-icon>
        </button>
        <div *ngIf="showSpeedMenu" class="speed-menu speed-menu-down" (mouseleave)="showSpeedMenu = false">
          <button
            *ngFor="let speed of speedOptions"
            class="speed-option-btn"
            (click)="setPlaybackSpeed(speed)"
          >
            <span class="speed-option-item" [class.active]="speed === playbackSpeed">
              {{ speed === 1 ? 'Normal' : speed + 'x' }}
            </span>
          </button>
        </div>
      </div>

    </div>

    <!-- Scrollable transcript text (karaoke reveal + auto-scroll) -->
    <div #transcriptScroll class="transcript-scroll">
      <p
        *ngFor="let seg of transcriptVM; let i = index"
        #segmentEl
        class="transcript-seg"
        [class.active]="i === activeSegmentIndex"
      >
        <span
          *ngFor="let w of seg.words"
          class="tw"
          [class.on]="isWordSpoken(w)"
          [class.current]="isWordActive(w)"
        >{{ w.text }} </span>
      </p>
    </div>

    <!-- ── Desktop control bar (single row) ─────────────────────────── -->
    <div class="transcript-controls hidden lg:flex items-center gap-4">

      <!-- Volume (icon only) -->
      <button
        class="ctrl-btn ctrl-sm"
        (click)="toggleMute()"
        aria-label="Bajar volumen"
      >
        <ion-icon [name]="volumeIcon"></ion-icon>
      </button>

      <!-- Transport -->
      <div class="flex items-center justify-center gap-3" aria-label="Controles de reproducción">
        <button class="ctrl-btn ctrl-md" (click)="skipBack()" aria-label="Retroceder 15 segundos">
          <ion-icon name="play-skip-back"></ion-icon>
        </button>
        <button
          class="ctrl-btn ctrl-lg ctrl-primary"
          (click)="togglePlayPause()"
          [attr.aria-label]="isPlaying ? 'Pausar' : 'Reproducir'"
        >
          <ion-icon [name]="isPlaying ? 'pause' : 'play'" [style.marginLeft.px]="isPlaying ? 0 : 2"></ion-icon>
        </button>
        <button class="ctrl-btn ctrl-md" (click)="skipForward()" aria-label="Adelantar 15 segundos">
          <ion-icon name="play-skip-forward"></ion-icon>
        </button>
      </div>

      <!-- Time + progress + time (fills remaining space) -->
      <div class="flex items-center gap-3 flex-1 min-w-0">
        <span class="timestamp-label flex-shrink-0">{{ formatTime(currentTime) }}</span>
        <div class="progress-wrap flex-1">
          <div class="progress-track">
            <div class="progress-filled" [style.width.%]="progress"></div>
            <div class="progress-thumb" [style.left.%]="progress"></div>
          </div>
          <input
            type="range"
            class="progress-range-overlay"
            [value]="currentTime"
            [min]="0"
            [max]="duration || 1"
            step="0.1"
            (input)="onSeek($event)"
            aria-label="Progreso de audio"
          />
        </div>
        <span class="timestamp-label flex-shrink-0">{{ formatTime(duration) }}</span>
      </div>

      <!-- Speed -->
      <div class="speed-control-wrap flex-shrink-0">
        <button
          class="ctrl-btn ctrl-sm ctrl-purple"
          (click)="toggleSpeedMenu()"
          aria-label="Cambiar velocidad de reproducción"
        >
          <mat-icon class="mdi mdi-speedometer speed-icon"></mat-icon>
        </button>
        <div *ngIf="showSpeedMenu" class="speed-menu" (mouseleave)="showSpeedMenu = false">
          <button
            *ngFor="let speed of speedOptions"
            class="speed-option-btn"
            (click)="setPlaybackSpeed(speed)"
          >
            <span class="speed-option-item" [class.active]="speed === playbackSpeed">
              {{ speed === 1 ? 'Normal' : speed + 'x' }}
            </span>
          </button>
        </div>
      </div>

      <!-- Transcript (active — closes the panel) -->
      <button
        class="ctrl-btn ctrl-sm ctrl-primary flex-shrink-0"
        (click)="toggleTranscript()"
        aria-label="Cerrar transcripción"
      >
        <ion-icon name="document-text"></ion-icon>
      </button>

    </div><!-- /desktop controls -->

    <!-- ── Mobile control bar (transport row + progress row) ────────── -->
    <div class="transcript-controls lg:hidden flex flex-col gap-[6px] lg:gap-3">

      <div class="grid items-center grid-cols-[auto_1fr_auto] gap-3">
        <!-- Volume -->
        <button
          class="ctrl-btn ctrl-sm"
          (click)="toggleMute()"
          aria-label="Bajar volumen"
        >
          <ion-icon [name]="volumeIcon"></ion-icon>
        </button>

        <!-- Transport -->
        <div class="flex items-center justify-center gap-3">
          <button class="ctrl-btn ctrl-md" (click)="skipBack()" aria-label="Retroceder 15 segundos">
            <ion-icon name="play-skip-back"></ion-icon>
          </button>
          <button
            class="ctrl-btn ctrl-lg ctrl-primary"
            (click)="togglePlayPause()"
            [attr.aria-label]="isPlaying ? 'Pausar' : 'Reproducir'"
          >
            <ion-icon [name]="isPlaying ? 'pause' : 'play'" [style.marginLeft.px]="isPlaying ? 0 : 2"></ion-icon>
          </button>
          <button class="ctrl-btn ctrl-md" (click)="skipForward()" aria-label="Adelantar 15 segundos">
            <ion-icon name="play-skip-forward"></ion-icon>
          </button>
        </div>

        <!-- Transcript (active — closes the panel) -->
        <button
          class="ctrl-btn ctrl-sm ctrl-primary"
          (click)="toggleTranscript()"
          aria-label="Cerrar transcripción"
        >
          <ion-icon name="document-text"></ion-icon>
        </button>
      </div>

      <!-- Progress -->
      <div class="flex flex-col gap-1">
        <div class="progress-wrap">
          <div class="progress-track">
            <div class="progress-filled" [style.width.%]="progress"></div>
            <div class="progress-thumb" [style.left.%]="progress"></div>
          </div>
          <input
            type="range"
            class="progress-range-overlay"
            [value]="currentTime"
            [min]="0"
            [max]="duration || 1"
            step="0.1"
            (input)="onSeek($event)"
            aria-label="Progreso de audio"
          />
        </div>
        <div class="flex justify-between">
          <span class="timestamp-label">{{ formatTime(currentTime) }}</span>
          <span class="timestamp-label">{{ formatTime(duration) }}</span>
        </div>
      </div>

    </div><!-- /mobile controls -->

  </div><!-- /transcript -->

</div>
